function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { PageHeader, Ribbon, Card, Empty, Search, Pending, Segmented } from "../../components/analytics-kit";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import ServiceFactory from "../../service";
import PlunkDropDown from "../../components/plunk-dropdown";
import { num, titleCase, cleanTitle } from "../sales/territory-common";
import { getGlobalRange, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import { hasTenantFeature } from "../../functions/tenant-features";

/*
 * Options (Product Configurations): which fabrics, finishes, legs and other
 * options people choose, and on which styles (PRODUCT_CONFIGURATIONS.md §2.4;
 * redesigned 2026-10-09 to the approved "Options" mockup, styles in
 * sass/v2/_products-more.scss, class prefix pm-).
 *
 * Embedded as the "configs" grain of the Product Explorer (which supplies the
 * header and tabs) and routed standalone at /Configurations/:range.
 *
 * GET /v1/product/configurations/{range}/summary → whole-range strip + `kinds`
 * GET /v1/product/configurations/{range}?$groupBy=configuration|style|<kind>&$top&$skip
 *   rows {styleId,styleTitle,sku,category,imageLink,componentCode,componentKind,
 *   componentName,units,revenue,orders,styleCount?}; `count` in every response.
 *
 * Notes kept from the build (2026-08-10 probes):
 * - `categoryId` is a NO-OP server-side, so the "On" product-type select
 *   filters CLIENT-SIDE over the loaded rows.
 * - `category` arrives HTML-encoded ("Furniture &gt; Sofas"), decoded here.
 * - Kind rows carry styleCount; older services do not, so "Styles" falls back
 *   to distinct styles per component from the loaded configuration pairs.
 * - The option list is DATA-DRIVEN from the summary's `kinds`.
 * - "Orders with options" as a % needs de-duplicated revenue of styles sold
 *   with an option. The summary's configuredRevenue counts a frame once per
 *   option, so the % is computed only when every pair and style row is loaded
 *   (exact); otherwise the tile shows the option dollars without a %.
 */

const PAGE = 200;
function svc(api) {
  const Service = ServiceFactory.getType(api, "product");
  return new Service();
}
async function safe(promise, fallback) {
  try {
    return (await promise) || fallback;
  } catch (e) {
    return fallback;
  }
}
function decodeEntities(s) {
  return String(s || "").replace(/&gt;/g, ">").replace(/&lt;/g, "<").replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}
// ALL-CAPS server names read as shouting: title-case them for display only.
function displayName(s) {
  const v = decodeEntities(s).replace(/^[A-Z_]{2,}:/, "").trim();
  return /[A-Z]/.test(v) && v === v.toUpperCase() ? titleCase(v) : v;
}
function styleName(r) {
  const t = cleanTitle(decodeEntities(r.styleTitle || ""));
  if (!t) return "";
  return /\d/.test(t) && !/\s/.test(t) ? t : displayName(t);
}
function styleCode(r) {
  if (r.sku) return r.sku;
  return /^shopify:/i.test(String(r.styleId || "")) ? "" : String(r.styleId || "");
}
function leaf(cat) {
  const parts = decodeEntities(cat).split(">");
  return parts[parts.length - 1].trim();
}
// Headline money: no cents. $1.92M / $988K / $1,203.
function money(v) {
  const n = Number(v) || 0;
  const a = Math.abs(n);
  if (a >= 1e6) return `$${(n / 1e6).toFixed(a >= 1e7 ? 1 : 2).replace(/\.?0+$/, "")}M`;
  if (a >= 1e4) return `$${Math.round(n / 1e3).toLocaleString("en-US")}K`;
  return `$${Math.round(n).toLocaleString("en-US")}`;
}

// Plain words for the option kinds the grammars emit. [menu label, noun in a sentence]
const KIND_WORDS = {
  fabric: ["Fabric", "fabric"],
  cover: ["Fabric", "fabric"],
  leather: ["Leather", "leather"],
  finish: ["Finish", "finish"],
  leg: ["Legs", "leg"],
  legs: ["Legs", "leg"],
  caster: ["Casters", "caster"],
  casters: ["Casters", "caster"],
  skirt: ["Skirt", "skirt"],
  cushion: ["Cushion", "cushion"],
  pillow: ["Pillows", "pillow"],
  pillows: ["Pillows", "pillow"],
  trim: ["Trim", "trim"],
  welt: ["Welt", "welt"],
  nail_head: ["Nailhead", "nailhead"],
  nailhead: ["Nailhead", "nailhead"],
  outdoor: ["Outdoor", "outdoor option"],
  hardware: ["Hardware", "hardware"],
  base: ["Base", "base"],
  arm: ["Arms", "arm"],
  back: ["Back", "back"],
  size: ["Size", "size"]
};
function kindWords(kind, kinds) {
  const k = String(kind || "").toLowerCase();
  // "Cover" is the named fabric or leather on the frame; when the data also has
  // fabric yardage lines, call it "Cover fabric" so the two menu entries differ.
  if (k === "cover" && (kinds || []).some(x => String(x).toLowerCase() === "fabric")) return ["Cover fabric", "cover fabric"];
  if (KIND_WORDS[k]) return KIND_WORDS[k];
  const w = titleCase(k.replace(/[_-]+/g, " "));
  return [w, w.toLowerCase()];
}
const FALLBACK_KINDS = ["fabric", "finish"];
function ShareBar({
  pct,
  max
}) {
  const w = max > 0 ? Math.max(2, Math.min(100, pct / max * 100)) : 0;
  return /*#__PURE__*/React.createElement("span", {
    className: "pm-share",
    title: `${Math.round(pct)}%`
  }, /*#__PURE__*/React.createElement("span", {
    className: "pm-bar"
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      width: `${w}%`
    }
  })), /*#__PURE__*/React.createElement("span", {
    className: "pm-share-n"
  }, Math.round(pct), "%"));
}
class ProductConfigurationsComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "openStyle", (styleId, title) => {
      if (!styleId || !hasTenantFeature("territoryRep")) return;
      this.props.history.push({
        pathname: `/Territory/${this.range()}/product/${encodeURIComponent(styleId)}`,
        state: {
          productTitle: title || ""
        }
      });
    });
    this.state = {
      summary: null,
      // summary endpoint (null = not available)
      tabs: null,
      // {key: {items, count}} lazy per-kind / style cache
      pairs: null,
      // style + option pairs
      error: false,
      loadingTab: false,
      view: "option",
      // option | pairs | style
      kind: "",
      // selected option kind ("" = first by revenue)
      pairsKind: "",
      // pairs view: "" = all options
      category: "",
      q: ""
    };
    this._alive = true;
  }
  range() {
    return this.props.params && this.props.params.range || getGlobalRange("this_year");
  }
  reset() {
    return {
      summary: null,
      tabs: null,
      pairs: null,
      error: false,
      category: "",
      q: ""
    };
  }
  async componentDidMount() {
    if (Auth.isAuthenticated() || (await Auth.attachOrSignIn())) this.load();
    this._periodTok = subscribePeriod(({
      range
    }) => {
      if (!this._alive || !range) return;
      // Embedded in the Product Explorer: reload in place, don't route away.
      if (this.props.embedded) {
        this.setState(this.reset(), () => this.load());
        return;
      }
      const target = `/Configurations/${range}`;
      if (window.location.pathname === target) {
        this.setState(this.reset(), () => this.load());
      } else {
        this.props.history.push(target);
      }
    });
  }
  componentWillUnmount() {
    this._alive = false;
    unsubscribePeriod(this._periodTok);
  }
  componentDidUpdate(prev) {
    const prevRange = prev.params && prev.params.range || "";
    const curRange = this.props.params && this.props.params.range || "";
    if (prevRange !== curRange) {
      this.setState(this.reset());
      this.load();
    }
  }
  async load() {
    const range = this.range();
    // The strip is ONE summary call (never page math); its `kinds` drive the
    // Option menu. The style page gives "of N selling". All fail soft.
    const [summary, pairs, styles] = await Promise.all([safe(svc(`/product/configurations/${range}/summary`).getAsync(), null), safe(svc(`/product/configurations/${range}?$groupBy=configuration&$top=${PAGE}`).getAsync(), {
      items: []
    }), safe(svc(`/product/configurations/${range}?$groupBy=style&$top=${PAGE}`).getAsync(), {
      items: []
    })]);
    if (!this._alive) return;
    this.setState({
      summary: summary && (summary.configuredPairs != null || (summary.kinds || []).length) ? summary : null,
      pairs: {
        items: pairs.items || [],
        count: pairs.count != null ? pairs.count : (pairs.items || []).length
      },
      tabs: {
        style: {
          items: styles.items || [],
          count: styles.count != null ? styles.count : (styles.items || []).length
        }
      }
    }, () => this.fetchTab(this.activeKind()));
  }
  kinds() {
    const {
      summary
    } = this.state;
    if (!summary || !(summary.kinds || []).length) return FALLBACK_KINDS;
    return (summary.kinds || []).filter(k => k && k.kind && ((Number(k.components) || 0) > 0 || (Number(k.revenue) || 0) > 0)).sort((x, y) => (Number(y.revenue) || 0) - (Number(x.revenue) || 0)).map(k => String(k.kind));
  }
  kindOptions() {
    const ks = this.kinds();
    return ks.map(k => [k, kindWords(k, ks)[0]]);
  }
  activeKind() {
    const ks = this.kinds();
    return ks.includes(this.state.kind) ? this.state.kind : ks[0] || "fabric";
  }
  async fetchTab(key, skip) {
    if (!key) return;
    if (key === "configuration") {
      if (skip == null) return; // first page loads in load()
      this.setState({
        loadingTab: true
      });
      const d = await safe(svc(`/product/configurations/${this.range()}?$groupBy=configuration&$top=${PAGE}&$skip=${skip}`).getAsync(), {
        items: []
      });
      if (!this._alive) return;
      this.setState(st => ({
        loadingTab: false,
        pairs: {
          items: [...(st.pairs.items || []), ...(d.items || [])],
          count: d.count != null ? d.count : st.pairs.count
        }
      }));
      return;
    }
    const cached = (this.state.tabs || {})[key];
    if (cached && skip == null) return;
    this.setState({
      loadingTab: true
    });
    const d = await safe(svc(`/product/configurations/${this.range()}?$groupBy=${encodeURIComponent(key)}&$top=${PAGE}${skip ? `&$skip=${skip}` : ""}`).getAsync(), {
      items: []
    });
    if (!this._alive) return;
    this.setState(st => {
      const prev = skip ? ((st.tabs || {})[key] || {
        items: []
      }).items : [];
      return {
        loadingTab: false,
        tabs: {
          ...st.tabs,
          [key]: {
            items: [...prev, ...(d.items || [])],
            count: d.count != null ? d.count : prev.length + (d.items || []).length
          }
        }
      };
    });
  }

  // Per component, from the LOADED pairs: distinct styles + the styles it sells on most.
  pairIndex() {
    const idx = {};
    ((this.state.pairs || {}).items || []).forEach(r => {
      if (!r.componentCode || !r.styleId) return;
      const e = idx[r.componentCode] = idx[r.componentCode] || {
        styles: new Set(),
        top: []
      };
      e.styles.add(String(r.styleId));
      e.top.push(r);
    });
    Object.values(idx).forEach(e => e.top.sort((a, b) => (Number(b.revenue) || 0) - (Number(a.revenue) || 0)));
    return idx;
  }
  filterRows(rows, nameOf) {
    const {
      category,
      q
    } = this.state;
    const term = q.trim().toLowerCase();
    return rows.filter(r => (!category || decodeEntities(r.category) === category) && (!term || nameOf(r).toLowerCase().includes(term) || String(r.componentCode || "").toLowerCase().includes(term) || String(r.sku || r.styleId || "").toLowerCase().includes(term)));
  }
  styleCell(r) {
    const drill = hasTenantFeature("territoryRep") && r.styleId;
    const code = styleCode(r);
    const name = styleName(r);
    return /*#__PURE__*/React.createElement("span", {
      className: `pm-style${drill ? " drill" : ""}`,
      onClick: () => this.openStyle(r.styleId, r.styleTitle)
    }, r.imageLink ? /*#__PURE__*/React.createElement("img", {
      className: "pm-thumb",
      src: r.imageLink,
      alt: "",
      loading: "lazy",
      onError: e => {
        e.target.style.visibility = "hidden";
      }
    }) : /*#__PURE__*/React.createElement("span", {
      className: "pm-thumb",
      "aria-hidden": "true"
    }), /*#__PURE__*/React.createElement("b", null, code && name && code !== name ? `${code} · ${name}` : name || code || "Unnamed style"));
  }

  // One row per option NAME within the kind. Several codes can carry the same name (a customer's-own-
  // material fabric gets a new code on every order), and listing "Com Fabric" three times reads as a bug.
  mergeByName(rows, idx) {
    const by = new Map();
    rows.forEach(r => {
      const name = displayName(r.componentName) || r.componentCode || "Unnamed";
      const k = name.toLowerCase();
      let m = by.get(k);
      if (!m) {
        m = {
          ...r,
          _name: name,
          _codes: [],
          _styles: new Set(),
          _top: [],
          _styleSum: 0,
          revenue: 0,
          orders: 0
        };
        by.set(k, m);
      }
      m._codes.push(r.componentCode);
      m.revenue = (Number(m.revenue) || 0) + (Number(r.revenue) || 0);
      m.orders = (Number(m.orders) || 0) + (Number(r.orders) || 0);
      const e = idx[r.componentCode];
      if (e) {
        e.styles.forEach(x => m._styles.add(x));
        m._top = m._top.concat(e.top);
      }
      if (r.styleCount != null) m._styleSum += Number(r.styleCount) || 0;
    });
    return [...by.values()].map(m => {
      m._top.sort((a, b) => (Number(b.revenue) || 0) - (Number(a.revenue) || 0));
      m._styleN = m._styles.size || m._styleSum || null;
      return m;
    }).sort((a, b) => (Number(b.orders) || 0) - (Number(a.orders) || 0) || (Number(b.revenue) || 0) - (Number(a.revenue) || 0));
  }
  renderOptions(rowsIn, kind, kindNoun) {
    const idx = this.pairIndex();
    const rows = this.mergeByName(rowsIn, idx);
    const totalOrders = rows.reduce((s, r) => s + (Number(r.orders) || 0), 0) || 1;
    const shares = rows.map(r => (Number(r.orders) || 0) / totalOrders * 100);
    const maxShare = Math.max(0, ...shares);
    const showOn = rows.some(r => r._top.length);
    return /*#__PURE__*/React.createElement("div", {
      className: `pm-table pm-options${showOn ? "" : " no-on"}`,
      role: "table"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pm-th",
      role: "row"
    }, /*#__PURE__*/React.createElement("span", null, kindWords(kind, this.kinds())[0]), /*#__PURE__*/React.createElement("span", null, "Share of ", kindNoun, " orders"), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "Revenue"), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "Orders"), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "Styles"), showOn ? /*#__PURE__*/React.createElement("span", null, "Most often on") : null), rows.map((r, i) => {
      const styles = r._styleN;
      const seen = new Set();
      const on = r._top.filter(p => {
        const k = String(p.styleId);
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      }).slice(0, 2).map(p => [styleCode(p), styleName(p)].filter(Boolean).join(" ")).join(", ");
      const codes = r._codes.filter(Boolean);
      return /*#__PURE__*/React.createElement("div", {
        className: "pm-tr",
        role: "row",
        key: `${r._name}|${i}`
      }, /*#__PURE__*/React.createElement("span", {
        className: "pm-opt"
      }, /*#__PURE__*/React.createElement("span", {
        className: "pm-swatch",
        "aria-hidden": "true"
      }), /*#__PURE__*/React.createElement("b", {
        title: codes.length > 1 ? `${codes.length} codes: ${codes.join(", ")}` : codes[0] || ""
      }, r._name)), /*#__PURE__*/React.createElement(ShareBar, {
        pct: shares[i],
        max: maxShare
      }), /*#__PURE__*/React.createElement("span", {
        className: "n"
      }, money(r.revenue)), /*#__PURE__*/React.createElement("span", {
        className: "n"
      }, num(Math.round(Number(r.orders) || 0))), /*#__PURE__*/React.createElement("span", {
        className: "n"
      }, styles != null ? num(styles) : ""), showOn ? /*#__PURE__*/React.createElement("span", {
        className: "pm-muted pm-on",
        title: on
      }, on) : null);
    }));
  }
  renderPairs(rows) {
    const ks = this.kinds();
    const total = rows.reduce((s, r) => s + (Number(r.revenue) || 0), 0) || 1;
    const shares = rows.map(r => (Number(r.revenue) || 0) / total * 100);
    const maxShare = Math.max(0, ...shares);
    return /*#__PURE__*/React.createElement("div", {
      className: "pm-table pm-pairs",
      role: "table"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pm-th",
      role: "row"
    }, /*#__PURE__*/React.createElement("span", null, "Style"), /*#__PURE__*/React.createElement("span", null, "Option"), /*#__PURE__*/React.createElement("span", null, "Product type"), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "Revenue"), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "Orders"), /*#__PURE__*/React.createElement("span", null, "Share of revenue")), rows.map((r, i) => /*#__PURE__*/React.createElement("div", {
      className: "pm-tr",
      role: "row",
      key: `${r.styleId}|${r.componentCode}|${i}`
    }, this.styleCell(r), /*#__PURE__*/React.createElement("span", {
      className: "pm-opt"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pm-swatch",
      "aria-hidden": "true"
    }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, displayName(r.componentName) || r.componentCode || "Unnamed"), r.componentKind ? /*#__PURE__*/React.createElement("span", {
      className: "pm-sub"
    }, kindWords(r.componentKind, ks)[0]) : null)), /*#__PURE__*/React.createElement("span", {
      className: "pm-muted"
    }, leaf(r.category)), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, money(r.revenue)), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, num(Math.round(Number(r.orders) || 0))), /*#__PURE__*/React.createElement(ShareBar, {
      pct: shares[i],
      max: maxShare
    }))));
  }
  renderStyles(rows) {
    return /*#__PURE__*/React.createElement("div", {
      className: "pm-table pm-styles",
      role: "table"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pm-th",
      role: "row"
    }, /*#__PURE__*/React.createElement("span", null, "Style"), /*#__PURE__*/React.createElement("span", null, "Product type"), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "Units"), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "Revenue"), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "Orders")), rows.map((r, i) => /*#__PURE__*/React.createElement("div", {
      className: "pm-tr",
      role: "row",
      key: `${r.styleId}|${i}`
    }, this.styleCell(r), /*#__PURE__*/React.createElement("span", {
      className: "pm-muted"
    }, leaf(r.category)), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, num(Math.round(Number(r.units) || 0))), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, money(r.revenue)), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, num(Math.round(Number(r.orders) || 0))))));
  }
  ribbon(kind) {
    const {
      summary,
      pairs,
      tabs
    } = this.state;
    const ks = this.kinds();
    const styleTab = tabs.style || {
      items: [],
      count: 0
    };
    const pairItems = pairs.items || [];
    const configured = new Set(pairItems.map(r => String(r.styleId)).filter(Boolean));
    const stylesWith = summary ? Number(summary.stylesSellingConfigured) || 0 : configured.size;
    const stylesSelling = Number(styleTab.count) || (styleTab.items || []).length;

    // Orders with options: exact only when every pair and style row is loaded.
    const allLoaded = pairItems.length >= (Number(pairs.count) || 0) && (styleTab.items || []).length >= stylesSelling;
    let tile1;
    if (allLoaded && (styleTab.items || []).length) {
      const all = styleTab.items.reduce((s, r) => s + (Number(r.revenue) || 0), 0);
      const withOpt = styleTab.items.reduce((s, r) => s + (configured.has(String(r.styleId)) ? Number(r.revenue) || 0 : 0), 0);
      tile1 = {
        l: "Orders with options",
        v: all ? `${Math.round(withOpt / all * 100)}%` : "0%",
        s: `of revenue, ${money(withOpt)}`,
        t: "Share of style revenue from styles sold with at least one option chosen"
      };
    } else {
      tile1 = {
        l: "Orders with options",
        v: money(summary ? summary.configuredRevenue : pairItems.reduce((s, r) => s + (Number(r.revenue) || 0), 0)),
        s: "sold with options chosen",
        t: "Revenue on lines sold with an option. A product with two options counts under each."
      };
    }
    const kindRows = (tabs[kind] || {}).items || [];
    const top = kindRows[0];
    const noun = kindWords(kind, ks)[1];
    const idx = top ? this.pairIndex()[top.componentCode] : null;
    const topStyles = top ? top.styleCount != null ? Number(top.styleCount) : idx ? idx.styles.size : null : null;
    return /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      cols: 4,
      tiles: [tile1, {
        l: "Options chosen",
        v: num(summary ? summary.componentsSeen : new Set(pairItems.map(r => r.componentCode)).size),
        s: "fabrics, finishes, legs and more"
      }, {
        l: `Most chosen ${noun}`,
        v: top ? displayName(top.componentName) || top.componentCode : "None yet",
        s: topStyles != null ? `on ${num(topStyles)} style${topStyles === 1 ? "" : "s"}` : ""
      }, {
        l: "Styles with options",
        v: num(stylesWith),
        s: stylesSelling ? `of ${num(stylesSelling)} selling` : ""
      }]
    });
  }
  render() {
    const {
      summary,
      pairs,
      tabs,
      error,
      category,
      loadingTab,
      view,
      q
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (pairs === null || tabs === null) return /*#__PURE__*/React.createElement(Loader, null);
    const ks = this.kinds();
    const kind = this.activeKind();
    const [, kindNoun] = kindWords(kind, ks);
    const cur = view === "pairs" ? pairs : view === "style" ? tabs.style || {
      items: [],
      count: 0
    } : tabs[kind] || {
      items: [],
      count: 0
    };
    let rowsAll = cur.items || [];
    if (view === "pairs" && this.state.pairsKind) rowsAll = rowsAll.filter(r => String(r.componentKind || "") === this.state.pairsKind);
    const nameOf = view === "option" ? r => displayName(r.componentName) : view === "pairs" ? r => `${styleName(r)} ${displayName(r.componentName)}` : styleName;
    const rows = this.filterRows(rowsAll, nameOf);
    const cats = [...new Set(rowsAll.map(r => decodeEntities(r.category)).filter(Boolean))].sort();
    const loadedAll = (cur.items || []).length >= (Number(cur.count) || (cur.items || []).length);
    const empty = summary ? (Number(summary.configuredPairs) || 0) === 0 && !(summary.kinds || []).length : (pairs.items || []).length === 0 && Object.keys(tabs).filter(k => k !== "style").every(k => !(tabs[k].items || []).length) && Object.keys(tabs).length > 1;
    const listWords = [...new Set(ks.map(k => kindWords(k, ks)[0]))];
    const listText = listWords.length > 1 ? `${listWords.slice(0, -1).join(", ")} and ${listWords[listWords.length - 1]}` : listWords[0];
    const title = view === "option" ? "Most chosen options" : view === "pairs" ? "Styles and their options" : "Styles";
    const loadKey = view === "pairs" ? "configuration" : view === "style" ? "style" : kind;
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view pm-view"
    }, !this.props.embedded && /*#__PURE__*/React.createElement(PageHeader, {
      title: "Options",
      subtitle: "Which fabrics, finishes and other options people choose, and on which styles"
    }), empty ? /*#__PURE__*/React.createElement(Empty, null, "No orders with options in this date range yet. Options fill in nightly as orders ship, so the current month may look light; earlier date ranges show the full picture.") : /*#__PURE__*/React.createElement("div", null, this.ribbon(kind), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: title,
      hint: `${num(rows.length)} of ${num(view === "pairs" && this.state.pairsKind ? rowsAll.length : cur.count || rowsAll.length)}`
    }, /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar pm-toolbar"
    }, /*#__PURE__*/React.createElement("label", {
      className: "pm-ctl"
    }, "Show by", /*#__PURE__*/React.createElement(Segmented, {
      value: view,
      onChange: v => this.setState({
        view: v,
        category: ""
      }),
      options: [["option", "Option"], ["pairs", "Style and option"], ["style", "Style"]]
    })), /*#__PURE__*/React.createElement("span", {
      className: "pm-grow"
    }), view === "option" && /*#__PURE__*/React.createElement("label", {
      className: "pm-ctl"
    }, "Option", /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "170px",
      value: kind,
      options: this.kindOptions(),
      onChange: v => this.setState({
        kind: v,
        category: ""
      }, () => this.fetchTab(v))
    })), view === "pairs" && /*#__PURE__*/React.createElement("label", {
      className: "pm-ctl"
    }, "Option", /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "170px",
      value: this.state.pairsKind,
      options: [["", "All options"], ...this.kindOptions()],
      onChange: v => this.setState({
        pairsKind: v,
        category: ""
      })
    })), cats.length > 1 && /*#__PURE__*/React.createElement("label", {
      className: "pm-ctl"
    }, "On", /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "200px",
      value: category,
      onChange: v => this.setState({
        category: v
      }),
      options: [["", "All product types"], ...cats.map(c => [c, leaf(c)])]
    })), /*#__PURE__*/React.createElement(Search, {
      value: q,
      onChange: v => this.setState({
        q: v
      }),
      placeholder: view === "style" ? "Search styles" : "Search options"
    })), loadingTab && !rowsAll.length ? /*#__PURE__*/React.createElement(Pending, null) : rows.length === 0 ? /*#__PURE__*/React.createElement(Empty, null, q.trim() ? "Nothing matches that search." : `Nothing in this date range${category ? " for this product type" : ""}.`) : view === "pairs" ? this.renderPairs(rows) : view === "style" ? this.renderStyles(rows) : this.renderOptions(rows, kind, kindNoun), /*#__PURE__*/React.createElement("div", {
      className: "pm-foot"
    }, /*#__PURE__*/React.createElement("span", null, view === "style" ? "Each style's own sales, before any options are added." : `The Option list covers ${listText}. A sofa ordered with fabric and casters counts once under each.`), !loadedAll && (cur.items || []).length > 0 && /*#__PURE__*/React.createElement("a", {
      href: "#",
      onClick: e => {
        e.preventDefault();
        if (!loadingTab) this.fetchTab(loadKey, (cur.items || []).length);
      }
    }, loadingTab ? "Loading…" : "Show more"))))));
  }
}
export const ProductConfigurationsViewComponent = withRouter(ProductConfigurationsComponent);
export default {
  ProductConfigurationsViewComponent
};